import { Eye, Pencil, Power, PowerOff, Trash2, RotateCcw, MoreHorizontal } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

/**
 * Common row-actions dropdown used across every admin master table.
 *
 * Props:
 * - status: "active" | "inactive" | "deleted" | any other string
 * - testId: optional prefix for data-testid attributes (falls back to "row-actions")
 * - onView, onEdit, onActivate, onDeactivate, onReactivate, onDelete, onRestore
 *   Any handler you don't pass will hide the corresponding menu item.
 *
 * Visibility rules per spec:
 *   status = active   → View, Edit, Deactivate, Delete
 *   status = inactive → View, Edit, Reactivate,  Delete
 *   status = deleted  → View, Restore  (no edit / delete on already deleted rows)
 */
export function RowActionsMenu({
  status = "active",
  testId = "row-actions",
  onView, onEdit,
  onActivate, onDeactivate, onReactivate,
  onDelete, onRestore,
  disabled = false,
}) {
  const s = (status || "").toLowerCase();
  const isDeleted = s === "deleted";
  const isInactive = s === "inactive" || s === "suspended";
  const isActive = !isDeleted && !isInactive;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 hover:text-[#101876] disabled:opacity-50"
          aria-label="Row actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-44">
        {onView && (
          <DropdownMenuItem onClick={onView} data-testid={`${testId}-view`}>
            <Eye className="mr-2 h-4 w-4" /> View
          </DropdownMenuItem>
        )}
        {!isDeleted && onEdit && (
          <DropdownMenuItem onClick={onEdit} data-testid={`${testId}-edit`}>
            <Pencil className="mr-2 h-4 w-4" /> Edit
          </DropdownMenuItem>
        )}
        {isActive && onDeactivate && (
          <DropdownMenuItem onClick={onDeactivate} data-testid={`${testId}-deactivate`}>
            <PowerOff className="mr-2 h-4 w-4" /> Deactivate
          </DropdownMenuItem>
        )}
        {isInactive && onReactivate && (
          <DropdownMenuItem onClick={onReactivate} data-testid={`${testId}-reactivate`}>
            <Power className="mr-2 h-4 w-4" /> Reactivate
          </DropdownMenuItem>
        )}
        {isInactive && !onReactivate && onActivate && (
          <DropdownMenuItem onClick={onActivate} data-testid={`${testId}-activate`}>
            <Power className="mr-2 h-4 w-4" /> Activate
          </DropdownMenuItem>
        )}
        {isDeleted && onRestore && (
          <DropdownMenuItem onClick={onRestore} data-testid={`${testId}-restore`}>
            <RotateCcw className="mr-2 h-4 w-4" /> Restore
          </DropdownMenuItem>
        )}
        {!isDeleted && onDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onDelete}
              className="text-rose-600 focus:text-rose-700"
              data-testid={`${testId}-delete`}
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
